import React, { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './ReviewsMultiProduct.css';
import { fetchBatchBVData, IReviewData, StarArray } from '../../foundation/StarArray/StarArray';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { CarouselSlider, ReviewsCard, Section, TextLine } from '../../importCL';
import { useWidthContext } from '../../providers/Width';
import Button from '../../component-library/atoms/Button';

const DEFAULT_IMAGE_WIDTH = 320;
const DEFAULT_IMAGE_HEIGHT = 202;
const DEFAULT_DESKTOP_WIDTH = 1024;
const DEFAULT_DESKTOP_HEIGHT = 1536;
const DEFAULT_MOBILE_WIDTH = 750;
const DEFAULT_MOBILE_HEIGHT = 566;

interface IReviewItem {
    quoteText: string;
    authorText?: string;
    authorUrl?: string;
    imagePath: string;
    imageAltText: string;
    lifestylePath?: string;
    mobileLifestylePath?: string;
    lifestyleAltText?: string;
    productName?: string;
    bazaarvoiceCode?: string;
    ctalink?: string;
    ctatext?: string;
}

interface IReviewsMultiProductProps extends MappedComponentProperties {
    headlineText?: string;
    reviewItem?: IReviewItem[];
    backgroundVariant?: '' | 'backImg' | 'solidColor';
    desktopImg?: string;
    mobileImg?: string;
    backgroundOverlay?: boolean;
    backgroundColor?: '' | 'white' | 'purple' | 'blue' | 'light-grey';
}

export const ReviewsMultiProductEditConfig = {
    emptyLabel: 'Reviews Multi Product',
    isEmpty: (props: PropsWithChildren<IReviewsMultiProductProps>) => {
        return !props || !props.reviewItem;
    },
};

const ReviewsMultiProduct = (props: PropsWithChildren<IReviewsMultiProductProps>): JSX.Element => {
    const [reviewData, setReviewData] = useState<IReviewData[]>([{ productId: '', average: 0, total: 0, overall: 0 }]);
    const { width } = useWidthContext();
    const darkBackground = (props.backgroundVariant === 'solidColor' && (props.backgroundColor === 'purple' || props.backgroundColor === 'blue'))
                           || props.backgroundVariant ==='backImg' ? true : false;
    useEffect(() => {
        const bazaarvoiceCodes = [];
        if (props?.reviewItem && props?.reviewItem?.length > 0) {
            props.reviewItem.forEach((reviewItem) => {
                if (reviewItem.bazaarvoiceCode) {
                    bazaarvoiceCodes.push(reviewItem.bazaarvoiceCode);
                }
            });
        }

        async function fetchReviewData() {
            await fetchBatchBVData(bazaarvoiceCodes).then(setReviewData);
        }

        if (bazaarvoiceCodes.length > 0) {
            fetchReviewData().catch(err => {
                console.debug('Error fetching review data', err);
            });
        }
    }, []);

    return (
        
        <Section 
            className='ReviewsMultiProduct !p-0'
            containerFluid= {true}
            containerClass='!p-0'
            bgColor={props.backgroundVariant === 'solidColor' ? props.backgroundColor : ''}
            bgOverlay= {props.backgroundVariant === 'backImg'? props.backgroundOverlay: ''}
            bgImg={props.backgroundVariant === 'backImg' ? (width > 786 ? props.desktopImg : props.mobileImg) :''}
        >
            {
                props.headlineText && 
                <TextLine className='ReviewsMultiProduct__mobileTitle' variant={darkBackground ? 'bubble-icon-light' :'bubble-icon'} textClass={`font-semibold ${darkBackground ? 'text-white': ''}`}>
                    {transformHTMLParserLinks(props.headlineText)}
                </TextLine>
            }
            <CarouselSlider
                className='ReviewsMultiProduct__carouselSlider'
                infinite
                slidesToShow={1}
                properties={{
                    slideGap: 500,
                }}
                customArrows= {{
                    arrowSize: 22,
                    arrowColor: darkBackground? 'white' : '#0a5ec0',
                }}
                slides={props.reviewItem && props.reviewItem.map((reviewItem, i) => {
                    const cardReviews = (reviewData as IReviewData[])?.find((d) => d.productId === reviewItem.bazaarvoiceCode);
                    
                    const lifestylePath = width > 769 ? reviewItem.lifestylePath :
                        reviewItem.mobileLifestylePath;

                    const img = { src: reviewItem.imagePath ?? lifestylePath,
                        alt: reviewItem.imageAltText ?? reviewItem.lifestyleAltText,
                        isLifestyle: reviewItem.imagePath ? false : true };
                    const imageWidth = img.isLifestyle
                        ? (width > 769 ? DEFAULT_DESKTOP_WIDTH : DEFAULT_MOBILE_WIDTH)
                        : DEFAULT_IMAGE_WIDTH;
                    const imageHeight = img.isLifestyle
                        ? (width > 769 ? DEFAULT_DESKTOP_HEIGHT : DEFAULT_MOBILE_HEIGHT)
                        : DEFAULT_IMAGE_HEIGHT;

                    return (
                        <div className='ReviewsMultiProduct__itemInfo' key={i}>
                            <div className={`ReviewsMultiProduct__productInfo ${img.isLifestyle ? 'ReviewsMultiProduct__productInfo--lifestyle': ''}`}>
                                {img.isLifestyle &&  <div className='ReviewsMultiProduct__lifestyleContainer'>
                                    <img className={`ReviewsMultiProduct__image
                                     ${img.isLifestyle ?
                            'ReviewsMultiProduct__image--lifestyle': ''}`} 
                                    src={img.src}
                                    alt={img.alt}
                                    loading='lazy'
                                    width={imageWidth}
                                    height={imageHeight}/>
                                </div>}
                                
                                {!img.isLifestyle && <>
                                    <img className={`ReviewsMultiProduct__image
                                     ${img.isLifestyle ?
                            'ReviewsMultiProduct__image--lifestyle': ''}`} 
                                    src={img.src}
                                    alt={img.alt}
                                    loading='lazy'
                                    width={imageWidth}
                                    height={imageHeight}/>
                                    <div className='ReviewsMultiProduct__productText'>
                                        <div className='ReviewsMultiProduct__productName'>{reviewItem.productName}</div>
                                        {cardReviews !== undefined &&
                                    <div className='ReviewsMultiProduct__reviews'>
                                        <StarArray size={20}
                                            average={cardReviews?.average ?? 0}
                                            total={cardReviews?.total ?? 0}
                                            overall={cardReviews?.overall ?? 0} />
                                        <div className='ReviewsMultiProduct__reviewsText'>
                                            {cardReviews?.average.toFixed(1)}/{cardReviews?.overall} | {cardReviews?.total.toLocaleString('en-US')} Reviews{' '}
                                        </div>
                                    </div>
                                        }
                                        {reviewItem.ctatext && 
                                        <LinkWrapper className='ReviewsMultiProduct__link colTwoRowFour flex' href={reviewItem.ctalink}> <Button size='medium' variant='link-light' icon='chevron'>{transformHTMLParserLinks(reviewItem.ctatext) as unknown as ReactElement}</Button></LinkWrapper>
                                        }
                                    </div>
                                </>
                                }
                            </div>
                            <ReviewsCard
                                cardVariant='pullquote'
                                className={`${props.headlineText ? '' : 'ReviewsMultiProduct__hiddenTitle'}`}
                                bgColor={props.backgroundVariant === 'solidColor' ? props.backgroundColor : ''}
                                bodyTextContainerClass= {darkBackground? 'text-white mb-6 text-2xl font-bold leading-2hr ' : ''}
                                reviewContent={{
                                    headLineText: props.headlineText,
                                    bodyText: reviewItem.quoteText,
                                    bottomLineText: (reviewItem.authorUrl ? <LinkWrapper href={reviewItem.authorUrl}>{reviewItem.authorText}</LinkWrapper> : reviewItem.authorText),
                                }}
                                headLineSettings={{
                                    textClass: `font-semibold ${darkBackground ? 'text-white': ''}`,
                                    variant: darkBackground ? 'bubble-icon-light' : 'bubble-icon',
                                }}
                                bottomLineSettings= {{
                                    textClass: darkBackground? 'text-white' : '',
                                    authorClass: darkBackground? 'ReviewsMultiProduct__authorBorder' : '',
                                }}
                            />
                        </div>
                    );
                })}>
            </CarouselSlider>
        </Section>
    );
};

export default ReviewsMultiProduct;
